import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { warehouseStockApi } from "../../../api/inventory/warehouse-stock";
import { ResourceLoading } from "../../utils/ResourceLoading";

export const ProductStock = ({ product }) => {
  const { t } = useTranslation();
  const [stockData, setStockData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (product?.id) {
      fetchProductStock();
    }
  }, [product?.id]);

  const fetchProductStock = async () => {
    setIsLoading(true);
    try {
      const response = await warehouseStockApi.getProductStock(product.id);
      if (!response.error) {
        setStockData(response.data);
      }
    } catch (error) {
      console.error("Error fetching product stock:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0";
    return parseFloat(num).toFixed(2);
  };

  if (isLoading) {
    return (
      <div className="shadow sm:overflow-hidden sm:rounded-md">
        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
          <ResourceLoading />
        </div>
      </div>
    );
  }

  return (
    <div className="shadow sm:overflow-hidden sm:rounded-md">
      <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
        <div>
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            {t("Product Stock")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Faktisk lagerbeholdning for produktet
          </p>
        </div>

        {stockData ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <dt className="text-sm font-medium text-blue-600">
                Lagerført antall
              </dt>
              <dd className="mt-1 text-2xl font-semibold text-blue-900">
                {formatNumber(stockData.tracked_quantity)}
              </dd>
            </div>

            <div className="bg-red-50 p-4 rounded-lg">
              <dt className="text-sm font-medium text-red-600">
                Utgående (plukket)
              </dt>
              <dd className="mt-1 text-2xl font-semibold text-red-900">
                {formatNumber(stockData.outgoing_quantity)}
              </dd>
            </div>

            <div className="bg-green-50 p-4 rounded-lg">
              <dt className="text-sm font-medium text-green-600">
                Innkommende
              </dt>
              <dd className="mt-1 text-2xl font-semibold text-green-900">
                {formatNumber(stockData.incoming_quantity)}
              </dd>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-300">
              <dt className="text-sm font-medium text-gray-700">
                Tilgjengelig
              </dt>
              <dd className="mt-1 text-2xl font-semibold text-gray-900">
                {formatNumber(stockData.available_quantity)}
              </dd>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            Ingen lagerdata tilgjengelig
          </div>
        )}

        {stockData?.storage_breakdown && stockData.storage_breakdown.length > 0 && (
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Oppdelt per lager:
            </h4>
            <div className="space-y-2">
              {stockData.storage_breakdown.map((storage, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center py-2 px-3 bg-gray-50 rounded"
                >
                  <span className="text-sm text-gray-700">
                    {storage.storage_title || `Lager ${storage.storage_id}`}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    {formatNumber(storage.quantity)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};